import type {
	ChangePasswordRequest,
	ChangePasswordResponse,
	LoginRequest,
	LoginResponse,
	LoginSuccess,
	MeResponse,
	MfaVerifyRequest,
	RoleCode,
	SwitchRoleResponse,
} from "@seagames/contracts";
import { queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryClient } from "../api/query-client";
import { clearTokens, setTokens } from "./token-store";

/**
 * `/auth/me` is the single source of truth for who is signed in.
 *
 * Deliberately NOT decoded from the JWT. The token's claims are a snapshot from
 * up to 15 minutes ago; `must_change_password` and the held-roles list can both
 * have moved since. The server re-reads them, so we ask the server.
 */
export const meQueryOptions = queryOptions({
	queryKey: ["auth", "me"] as const,
	queryFn: () => api.get<MeResponse>("/auth/me"),
	staleTime: 5 * 60_000,
	// Retry is inherited from the query client on purpose: it retries transient
	// failures only. Setting `retry: false` here would turn one blip on the way
	// to /auth/me into a full sign-in screen, which is the failure mode this
	// whole stack is built to avoid.
});

/**
 * Store the tokens from a completed sign-in and drop any cached session.
 *
 * The cache is NOT seeded from the login response: that response has no
 * `group`, and the shell's access check (`hasGmsAccess`) needs it — a seeded
 * `group: null` would show a scoped user who has a group the "no access"
 * page. The guard's `ensureQueryData` fetches `/auth/me` once instead.
 */
export function acceptLogin(result: LoginSuccess): void {
	setTokens({ accessToken: result.access_token, refreshToken: result.refresh_token });
	queryClient.removeQueries({ queryKey: meQueryOptions.queryKey });
}

/** `client_app`: the sign-in log says which app each sign-in came from. */
export const login = (body: LoginRequest) =>
	api.post<LoginResponse>("/auth/login", { ...body, client_app: "GMS" } satisfies LoginRequest, { auth: false });

/** Finish an MFA sign-in. The ticket stands in for the session that does not exist yet. */
export const verifyMfa = (body: MfaVerifyRequest) =>
	api.post<LoginSuccess>("/auth/mfa/verify", body, { auth: false });

export interface MfaSetup {
	secret: string;
	otpauth_uri: string;
	issuer: string;
	account: string;
	digits: number;
	period: number;
}

export const beginMfaSetup = (mfaTicket: string) =>
	api.post<MfaSetup>("/auth/mfa/setup", { mfa_ticket: mfaTicket }, { auth: false });

/**
 * Confirming enrolment returns the backup codes exactly once.
 *
 * When it was reached via a login ticket the sign-in completes here too, and
 * the API spreads the login result into the same object — hence the union
 * rather than a nested `login` key. `access_token` is the discriminant.
 */
export type MfaConfirmResponse =
	| ({ backup_codes: string[] } & LoginSuccess)
	| { backup_codes: string[]; access_token?: undefined };

export const confirmMfaSetup = (mfaTicket: string, code: string) =>
	api.post<MfaConfirmResponse>("/auth/mfa/confirm", { mfa_ticket: mfaTicket, code }, { auth: false });

export const isCompletedLogin = (r: MfaConfirmResponse): r is { backup_codes: string[] } & LoginSuccess =>
	r.access_token !== undefined;

export async function changePassword(body: ChangePasswordRequest): Promise<void> {
	const result = await api.post<ChangePasswordResponse>("/auth/change-password", body);
	// This is the token that drops the `pwc` claim — the gate opens here, not
	// when the frontend decides it should. Store it before anything re-fetches.
	setTokens({ accessToken: result.access_token });
	// Re-read the session, never just invalidate it: nothing on this page
	// observes `/auth/me` (it came from `ensureQueryData`), so an invalidation
	// refetches nothing and the shell's guard read the stale
	// `must_change_password: true` — straight back to this page.
	await queryClient.fetchQuery({ ...meQueryOptions, staleTime: 0 });
}

/** `userGroupId`: also move to that group (one of /auth/me.group_choices) — "Organization · NOC Brunei". */
export async function switchRole(role: RoleCode, userGroupId?: string): Promise<void> {
	const result = await api.post<SwitchRoleResponse>("/auth/switch-role", userGroupId ? { role, user_group_id: userGroupId } : { role });
	setTokens({ accessToken: result.access_token });
	// One active role per session (CLAUDE.md § Auth invariants). Every cached
	// list was fetched under the OLD role's permissions and may contain rows the
	// new role must not see, so the cache goes — not just the session entry.
	queryClient.clear();
	await queryClient.invalidateQueries({ queryKey: meQueryOptions.queryKey });
}

export async function logout(): Promise<void> {
	try {
		await api.post<null>("/auth/logout");
	} catch {
		// The session is being abandoned either way. A failed logout call must
		// not strand the user in a signed-in-looking UI.
	}
	clearTokens("user");
	queryClient.clear();
}
